import { useUserAgent } from '@pex/ug-ssr-server/hooks/use-ua';
import Cookie from 'js-cookie';

import { isInIOS, compareVersion, isInBrowser, isInWebview } from './env';

interface UAInfoType {
    applewebkit?: string;
    chrome?: string;
    mozilla?: string;
    safari?: string;
    kwai?: string;
    yoda?: string;
    build?: string;
    nettype?: string;
    version?: string;
    mobile?: string;
    ksnebula?: string;
    tbht?: string;
    /** 4tab下顶导的值 */
    ftsfht?: string;
    /** 4tab下底导的值 */
    fbsfht?: string;
    statusht?: string;
    /** 是否低端机 */
    islp?: string;
    /** 是否高崩溃机型 */
    icfo?: string;
    [key: string]: string | undefined;
}

interface IphoneEdition {
    main: number;
    sub: number;
}

export function uaGetInfo(): UAInfoType {
    const matchList = useUserAgent()?.match(/([a-zA-Z]+)\/(\S+)(:\s)?/g) ?? [];
    const info: UAInfoType = {};
    for (const str of matchList) {
        const res = str!.split('/');
        const key = res[0]!.toLowerCase();
        info[key] = res[1]!.split(',')[0];
    }
    return info;
}

export type BarInfo = {
    /**
     * 状态栏高度
     */
    statusBarHeight: number;
    /**
     * 操作栏高度
     */
    toolBarHeight: number;
    /**
     * 底部安全区高度
     */
    bottomSafeHeight: number;
    /**
     * 操作栏高度 + 设备安全区高度
     */
    bottomHeight: number;
};

const iosSafeMaxHeight = 38;

/**
 * 在第四 tab 下
 */
export function isAtFourTab() {
    const { tbht, ftsfht } = uaGetInfo();
    // tbht 非4tab下注入版本不知道是不是还要兼容
    return Boolean(ftsfht ?? tbht);
}

export function isAtLayoutType4() {
    return isInBrowser && isInWebview() ? window.location.search.includes('layoutType=4') : false;
}

export function isAtLiveWebView() {
    return isInBrowser ? window.location.search.includes('layoutType=3') : false;
}

function getBrowserType() {
    return Cookie.get('browseType') ?? '0';
}

function hasBottomNav() {
    return ['true', '1'].includes(Cookie.get('bottom_navigation') ?? '');
}

function getNewBottomAreaHeight() {
    const { fbsfht } = uaGetInfo();
    return fbsfht !== undefined ? Number(fbsfht) / window.devicePixelRatio : -1;
}

/** 是否处于沉浸状态 */
export function isInImmersiveTab() {
    if (isAtFourTab() && getNewBottomAreaHeight() > -1) {
        const bth = getNewBottomAreaHeight();
        const browserType = getBrowserType();
        if (!isInIOS()) {
            return bth === 0;
        } else {
            if (browserType === '3') {
                // 极速版底导实现不占位
                return bth < iosSafeMaxHeight;
            } else if (browserType === '4') {
                // 主App通过安全区来判断是不是沉浸式，没有安全区的机器就不得不放弃
                return bth > 0;
            }
        }
    }
    return false;
}

function mayNeedBarHeight() {
    // getNewBottomAreaHeight在新版安卓9.10.10之后存在，iOS返回安全区的高度，安卓返回为0的时候说明不存在透明底导的情况
    // 但是9.11.40后底导才开始占位
    // iOS安全区应该是34，这里写大了一些，防止可能有的iOS大于这个值
    const newBottomAreaHeight = getNewBottomAreaHeight();
    const nebulaVersion = uaGetInfo().ksnebula ?? '';
    const poorAndroid = compareVersion(nebulaVersion, '9.11.40') === 'lt';
    return Boolean(
        newBottomAreaHeight < 0 ||
            (isInIOS() && newBottomAreaHeight > iosSafeMaxHeight) ||
            (!isInIOS() && newBottomAreaHeight > 0 && poorAndroid && nebulaVersion),
    );
}

function hasTransparentToolbar() {
    return hasBottomNav() && getBrowserType() === '3' && isAtFourTab() && mayNeedBarHeight();
}

export function getPhoneModelFromCookie() {
    return Cookie.get('mod');
}

export function getPhoneModel(model?: string): IphoneEdition {
    const phoneModel = model ?? getPhoneModelFromCookie();
    if (phoneModel) {
        const infos = phoneModel.split(',');
        if (infos[0] !== undefined) {
            return {
                main: +infos[0].replace(/\D/g, ''),
                sub: infos[1] ? +infos[1] : 0,
            };
        }
    }
    return {
        main: 0,
        sub: 0,
    };
}

function noSafeAreaInsetBottom() {
    if (!isInIOS()) {
        return false;
    }
    const { main, sub } = getPhoneModel();
    if (main > 0) {
        return main < 10 || (main === 10 && sub !== 3 && sub !== 6) || (main === 12 && sub === 8);
    }
    return false;
}

function getToolbarHeight() {
    return hasTransparentToolbar() ? 49 : 0;
}

function getBottomSafeAreaHeight() {
    return noSafeAreaInsetBottom() ? 0 : 34;
}

const IOSStatusBarDefaultHeightsMap = {
    '48': ['iPhone11,8', 'iPhone12,1'],
    '47': ['iPhone13,2', 'iPhone13,3', 'iPhone13,4', 'iPhone14,2', 'iPhone14,3', 'iPhone14,4', 'iPhone14,5'],
    '44': [
        'iPhone10,3',
        'iPhone10,6',
        'iPhone11,2',
        'iPhone11,6',
        'iPhone11,4',
        'iPhone12,3',
        'iPhone12,5',
        'iPhone13,1',
    ],
    '30': ['iPhone7,1', 'iPhone8,2', 'iPhone9,2', 'iPhone9,4', 'iPhone10,2', 'iPhone10,5'],
};

function findKeyByValue(obj: Record<string, string[]>, value: string) {
    return Object.keys(obj).find((key) => obj[key]!.includes(value));
}

function getIOSDefaultStatusBarHeight() {
    return findKeyByValue(IOSStatusBarDefaultHeightsMap, getPhoneModelFromCookie() ?? '') ?? '20';
}

function getTopAreaHeight() {
    const { tbht, ftsfht, statusht } = uaGetInfo();
    let topGap = 0;
    if (tbht !== undefined || ftsfht !== undefined) {
        topGap = Number(ftsfht ?? tbht) / (isInBrowser ? window.devicePixelRatio : 2);
    }
    // 在直播间场景不需要statusht值
    else if (Number(statusht) !== 0 && !isAtLiveWebView() && statusht !== undefined) {
        topGap = Number(statusht);
    }
    // iOS App老版本高度取不到的话走兜底写死逻辑
    else if (isAtLayoutType4()) {
        topGap = +getIOSDefaultStatusBarHeight();
    }
    return topGap;
}

const toolBarHeight = 0;
const bottomHeight = -1;

export function useBarInfo(): BarInfo {
    const statusBarHeight = getTopAreaHeight();
    const bottomSafeHeight = getBottomSafeAreaHeight();
    return {
        statusBarHeight,
        toolBarHeight,
        bottomSafeHeight,
        bottomHeight,
    };
}
